import { useState } from 'react';
import { Link } from 'react-router-dom';

import { BackButton } from '@/components/stashstr/BackButton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

import { getTerritories, getTitle, isValidStashItem, toNevent } from '@/lib/stashstr';

export default function Search() {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');

  return (
    <div>
      <BackButton />
      <h1 className="text-[18px] font-bold text-gray-900 dark:text-gray-100 mb-3">search</h1>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(input.trim());
        }}
        className="flex gap-2 mb-4"
      >
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search posts…"
          className="text-[14px]"
          aria-label="Search query"
        />
        <Button
          type="submit"
          className="bg-[#f97316] hover:bg-[#ea580c] dark:bg-[#fb923c] dark:hover:bg-[#fdba74] dark:text-[#431407] text-white"
        >
          search
        </Button>
      </form>
      {query && <SearchResults query={query} />}
    </div>
  );
}

function SearchResults({ query }: { query: string }) {
  const { nostr } = useNostr();

  const { data, isLoading } = useQuery({
    queryKey: ['stashstr', 'search', query],
    queryFn: async (c) => {
      const signal = c.signal;
      let relayResults: NostrEvent[] = [];
      try {
        // NIP-50 relay-side search (supported on some relays only)
        relayResults = await nostr.query([{ kinds: [11], search: query, limit: 30 }], { signal });
      } catch {
        relayResults = [];
      }
      // Fallback / merge: client-side filter over recent posts
      const recent = await nostr.query([{ kinds: [11], limit: 200 }], { signal });
      const q = query.toLowerCase();
      const merged = new Map<string, NostrEvent>();
      for (const ev of [...relayResults, ...recent]) {
        if (!isValidStashItem(ev)) continue;
        const title = getTitle(ev).toLowerCase();
        const territories = getTerritories(ev).join(' ');
        if (title.includes(q) || territories.includes(q) || ev.content.toLowerCase().includes(q)) {
          merged.set(ev.id, ev);
        }
      }
      return [...merged.values()].sort((a, b) => b.created_at - a.created_at);
    },
  });

  if (isLoading) {
    return <div className="text-[13px] text-gray-500 dark:text-gray-400 py-4">Searching…</div>;
  }

  const posts = data ?? [];
  if (posts.length === 0) {
    return (
      <div className="border border-dashed border-gray-300 dark:border-[#3a3d41] rounded py-10 text-center text-[13px] text-gray-500 dark:text-gray-400">
        No results found. Try checking your relay connections or wait a moment for content to load.
      </div>
    );
  }

  return (
    <div>
      {posts.map((ev) => (
        <div key={ev.id} className="py-2 border-b border-gray-200 dark:border-[#2a2d30]">
          <Link
            to={`/items/${toNevent(ev)}`}
            className="text-[15px] font-semibold text-[#1a56db] dark:text-[#6ea8fe] hover:underline"
          >
            {getTitle(ev)}
          </Link>
          <div className="text-[12px] text-gray-500 dark:text-gray-400">
            {getTerritories(ev)
              .map((t) => `~${t}`)
              .join(' ')}
          </div>
        </div>
      ))}
    </div>
  );
}
